import { ComponentProps, useRef } from "react";
import FallbackPaywall from "@blocks/FallbackPaywall";
import DynamicImu from "@components/Advertisement/DynamicImu";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import DelayedComponentsLocationChange from "@components/DelayedComponents/DelayedComponentsLocationChange";
import ProgressBar from "@components/ProgressBar/ProgressBar";
import PromoteBTGController from "@features/banner/PromoteBTG/controller/PromoteBTGController";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { getVariantObject } from "@features/posthog/helpers/getVariantObject";
import { FEATURE_FLAG_LOAD_MORE_LOGIC } from "@features/posthog/utils/constants";
import TrendingCards from "@features/pulse/components/trendingCards/TrendingCards";
import Taboola from "@features/taboola/Taboola";
import PromoteCNTranslation from "@features/translations/PromoteCNTranslation";
import { useCountry } from "@hooks/useCountry";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useInjectClientData } from "@hooks/useInjectClientData";
import useIsAppWebView from "@hooks/useIsAppWebView";
import usePublishedTime from "@hooks/usePublishedTime";
import ArticleAttachments from "@routes/Article/components/ArticleAttachments";
import ArticleByline from "@routes/Article/components/ArticleByline/ArticleByline";
import ArticleMainMedia from "@routes/Article/components/ArticleMainMedia/ArticleMainMedia";
import ArticleBTGNewsletter from "@routes/Article/components/ArticleNewsletter/ArticleBTGNewsletter";
import ArticleParagraphElements from "@routes/Article/components/ArticleParagraphElements/ArticleParagraphElements";
import ArticlePurchaseLink from "@routes/Article/components/ArticlePurchaseLink/ArticlePurchaseLink";
import { Versions } from "@routes/Article/components/ArticlePurchaseLink/types";
import ArticleSubshareSent from "@routes/Article/components/ArticleSubshare/ArticleSubshareSent";
import BottomArticleSubshare from "@routes/Article/components/ArticleSubshare/BottomArticleSubshare";
import ArticleTags from "@routes/Article/components/ArticleTags";
import ArticleTopFold from "@routes/Article/components/ArticleTopFold";
import ArticleTopFoldPodcast from "@routes/Article/components/ArticleTopFoldPodcast";
import ChineseBanner from "@routes/Article/components/ChineseBanner";
import FeedbackCN from "@routes/Article/components/Feedback/FeedbackCN";
import PodcastAboutComponent from "@routes/Article/components/PodcastArticle/PodcastAboutComponent";
import PodcastArticleToolbarComponent from "@routes/Article/components/PodcastArticle/PodcastArticleToolbarComponent";
import { useHookUpdateUrlArticle } from "@routes/Article/hooks/useHookUpdateUrlArticle";
import { useNextPodcastArticles } from "@routes/Article/hooks/useNextPodcastArticles";
import { IS_DISPLAY_GIFT_AN_ARTICLE } from "@routes/Article/utils/constants";
import {
  ArticleAuthorType,
  ArticlePaidModeType,
} from "@routes/Article/utils/enums";
import {
  getCdpPaywallModuleId,
  getGAData,
  getIsBrandedContent,
  getTruncatedBody,
  getUrlPathFromAbsoluteUrl,
  isArticleExcludedFromPaywallBySection,
} from "@routes/Article/utils/helpers";
import { CommonArticleLayoutProps } from "@routes/Article/utils/types";
import { useSetReadingHistory } from "@routes/MyAccount/hooks/useSetReadingHistory";
import { IS_PLAYWRIGHT } from "@util/constant";
import { checkIfFromPaidPressRelease, cn } from "@util/helpers";

const MainNewsDefault = ({
  article,
  index = 0,
  gsChannels,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  alacrityRes,
  isModalOpen = false,
  setArticleObj,
  articleInView,
  displayArticleRef,
  isGifted = false,
  isPreview = false,
  isNavigate = false,
  isSummaryAvailable = false,
  isChinese = false,
  isTaboolaEnabled = false,
  isPodcastAudioArticle = false,
  trendingCards,
  variantObject,
}: CommonArticleLayoutProps): React.ReactElement => {
  const { isSubscriber, isAnonymous } = useUserInfo();
  const { isMoreThan60DaysAgo, isMoreThan30DaysAgo } = usePublishedTime(
    article?.publishTime
  );

  const loadMoreVariant = getVariantObject<string>({
    key: FEATURE_FLAG_LOAD_MORE_LOGIC,
    variantObject,
  });

  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    paidMode,
    authors,
    publishTime,
    editedTime,
    hasUpdatedTimeDisplayed,
    media,
    relatedAttachments,
    sections,
    sentiment,
    tags,
    availableTranslations,
    body,
    podcast,
    displayReadMoreAndNewsletterSignupForm,
  } = article;

  const { articles: podcastsComingUpNextData } = useNextPodcastArticles({
    urlPath: tags?.[0]?.urlPath,
    afterPublishTime: publishTime,
    isPodcastAudioArticle,
  });

  useSetReadingHistory({ articleId: id });
  const urlPath = getUrlPathFromAbsoluteUrl(article.urlPath || "");

  const { ref } = useHookUpdateUrlArticle(
    urlPath,
    isNavigate,
    article,
    isChinese,
    loadMoreVariant?.variantKey,
    setArticleObj
  );

  const { gsChannels: gsChannelsAPI } = useGrapeshotData({
    urlPath: urlPath,
    index,
    keywords: sentiment?.keywords,
  });

  const isAppWebView = useIsAppWebView();
  const { isSGUser } = useCountry();

  const isPremium = paidMode === ArticlePaidModeType.PREMIUM;
  const mainSection = sections?.[0];

  const articleContentProgress = useRef<HTMLDivElement>(null);

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");
  const isPaidPressRelease = checkIfFromPaidPressRelease(
    article?.sections || []
  );

  const isTruncated60DaysAgo =
    !isSubscriber && !isPremium && isMoreThan60DaysAgo;

  const isTruncated30DaysAgo = isAnonymous && !isPremium && isMoreThan30DaysAgo;

  const isFullContent =
    !isSGUser ||
    isSubscriber || // User is a subscriber
    isGifted || // Article is gifted
    isPreview || // Article is in preview mode
    isArticleExcludedFromPaywallBySection(sections) ||
    isBrandedContent || // Article is branded content
    (!(isTruncated60DaysAgo || isTruncated30DaysAgo) && !isPremium); // User is not affected by registration wall and article is not premium;

  const cdpPaywallModuleId = getCdpPaywallModuleId({
    isTruncated30DaysAgo: isTruncated30DaysAgo || false,
    isTruncated60DaysAgo: isTruncated60DaysAgo || false,
  });

  const isAuthorOnly = !authors?.some(
    (author) => author.type === ArticleAuthorType.CONTRIBUTOR_PROFILE
  );
  const isBylineArticle = !!authors?.some(({ type }) =>
    [ArticleAuthorType.AUTHOR_PROFILE, ArticleAuthorType.AUTHOR].includes(type)
  );

  const purchaseArticleDetails = {
    isDisplayed: isBylineArticle,
    publishedDate: publishTime,
    title,
  };

  const partialElements = getTruncatedBody(body, 2);
  const bodyElements = isFullContent ? body : partialElements;

  const paragraphIndices = bodyElements
    .map((content, idx) => (content.startsWith("<p>") ? idx : null))
    .filter((idx) => idx !== null);
  const totalParagraphs = paragraphIndices.length;
  const isZhHansAvailable = availableTranslations?.includes("zh-hans");

  useInjectClientData({
    gaProps: getGAData(article, isChinese),
    gaVariant: loadMoreVariant?.variantKey,
  });

  const podcastAboutData = podcast?.tag;

  const articleTopFoldProps: ComponentProps<typeof ArticleTopFold> = {
    displayArticleRef,
    isSummaryAvailable,
    article,
    isPodcastAudioArticle,
    isPremium,
    handleGenerateGiftURLModal,
    isChinese,
    alacrityRes,
    setIsModalOpen,
    purchaseArticleDetails,
    isAuthorOnly,
  };

  return (
    <>
      <ArticleSubshareSent
        alacrityRes={alacrityRes}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <ProgressBar
        articleContentProgress={articleContentProgress}
        className={cn("top-[60px]")}
        isDisplayed={!isAppWebView && articleInView.id === article.id}
      />

      <article
        className={cn(
          "display-type--mainNewsDefault w-full py-5",
          "animate-slideIn",
          { "pt-0": isPodcastAudioArticle }
        )}
        data-index={index}
        data-cueid={id}
        data-url-path={urlPath}
        ref={ref}
      >
        <div ref={articleContentProgress} className="relative">
          {isPodcastAudioArticle ? (
            <ArticleTopFoldPodcast
              article={article}
              podcast={podcast}
              podcastsComingUpNextData={podcastsComingUpNextData}
            />
          ) : null}

          <div className="px-3 md:px-0 md:pt-4">
            <div className="mx-auto mb-4 w-full max-w-[760px]">
              <ArticleTopFold {...articleTopFoldProps} />

              <>
                {authors && isAuthorOnly && isPodcastAudioArticle ? (
                  <PodcastArticleToolbarComponent
                    article={article}
                    isPremium={isPremium}
                    handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                    setIsModalOpen={setIsModalOpen}
                  />
                ) : null}
              </>

              {!isChinese && !isPodcastAudioArticle ? (
                <PromoteCNTranslation
                  className="mb-6 md:px-8"
                  isTranslationAvailable={isZhHansAvailable}
                  link={urlPath}
                />
              ) : null}

              {!isPodcastAudioArticle ? (
                <ArticleMainMedia
                  media={media || []}
                  captionClassName={cn({ "font-notoSansSC": isChinese })}
                />
              ) : null}

              <>
                {!isAuthorOnly ? (
                  <div className="mb-6 pt-6 md:px-8">
                    <ArticleByline
                      authors={authors || []}
                      editedTime={editedTime}
                      updatedTime={publishTime}
                      hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                    />
                  </div>
                ) : null}
              </>

              <ArticleAttachments
                attachments={relatedAttachments || []}
                className="mb-6 md:mx-8"
              />

              <>
                {isChinese ? (
                  <ChineseBanner
                    className="mx-auto my-6 md:mx-8"
                    urlPath={article.urlPath}
                  />
                ) : null}
              </>

              <div
                className={cn(
                  "mx-auto my-4 font-lucida text-xl leading-8 -tracking-5% text-gray-850 md:mb-6 md:px-8",
                  {
                    "font-notoSansSC text-lg leading-relaxed tracking-2% md:text-xl":
                      isChinese,
                    "mb-4 md:mb-6 [&>*:last-child]:relative [&>*:last-child]:after:absolute [&>*:last-child]:after:inset-0 [&>*:last-child]:after:bg-gradient-to-t [&>*:last-child]:after:from-white [&>*:last-child]:after:to-transparent":
                      !isFullContent,
                  }
                )}
                data-testid="article-body-container"
              >
                <ArticleParagraphElements
                  body={bodyElements}
                  article={article}
                  adsWrapperClass={cn(
                    "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                    { "md:!my-12": IS_PLAYWRIGHT }
                  )}
                  gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
                  articleIndex={index}
                  isGifted={isGifted}
                  className={"mb-4 md:mb-6"}
                  isBrandedContent={isBrandedContent}
                  isPaidPressRelease={isPaidPressRelease}
                  paragraphIndices={paragraphIndices}
                  isFullContent={isFullContent}
                  isPodcastAudioArticle={isPodcastAudioArticle}
                />
              </div>

              {isFullContent ? (
                <>
                  {IS_DISPLAY_GIFT_AN_ARTICLE ? (
                    <BottomArticleSubshare
                      className="mb-6 md:px-8"
                      handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                      setIsModalOpen={setIsModalOpen}
                      alacrityRes={alacrityRes}
                      isPremium={isPremium}
                    />
                  ) : null}

                  {displayReadMoreAndNewsletterSignupForm ? (
                    <ArticleBTGNewsletter className="border-t border-gray-175 pb-6 pt-3 md:mx-8" />
                  ) : null}
                </>
              ) : null}

              <>{isChinese ? <FeedbackCN className="mb-6 md:mx-8" /> : null}</>

              <>
                {isFullContent ? (
                  <ArticleTags
                    tags={article?.tags}
                    className="mb-6 md:px-8"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}
              </>

              <>
                {publishTime && isFullContent ? (
                  <div className="no-print mb-6 border-t border-gray-175 py-3 md:mx-8">
                    <ArticlePurchaseLink
                      title={title}
                      publishedDate={publishTime}
                      versions={Versions.version2}
                      displayPurchase={purchaseArticleDetails.isDisplayed}
                      urlPath={urlPath}
                    />
                  </div>
                ) : null}
              </>

              <PromoteBTGController />

              <>
                {isSGUser ? (
                  <DelayedComponentsLocationChange
                    waitBeforeShow={1000}
                    urlPath={urlPath}
                  >
                    <CASBlock
                      cdpId={cdpPaywallModuleId}
                      index={index || 0}
                      className="no-print mb-6 mt-11 md:px-8 lg:-mx-6"
                      fallback={
                        !isSubscriber ? (
                          <FallbackPaywall
                            className="mb-6 transition duration-300 ease-in data-[closed]:opacity-0"
                            isPremium={isPremium}
                          />
                        ) : null
                      }
                    />
                  </DelayedComponentsLocationChange>
                ) : null}
              </>

              <>
                {!isFullContent ? (
                  <ArticleTags
                    tags={article.tags}
                    className="mb-8 mt-11 md:px-8 lg:-mx-6"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}

                {mainSection && !isPaidPressRelease ? (
                  <DynamicImu
                    index={index || 0}
                    totalParagraphs={totalParagraphs}
                    adsWrapperClass={cn(
                      "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                      { "md:!my-12": IS_PLAYWRIGHT }
                    )}
                    articleId={article?.id}
                    keywords={article?.tags}
                    isPodcastAudioArticle={isPodcastAudioArticle}
                  />
                ) : null}
              </>
            </div>

            {isPodcastAudioArticle && podcastAboutData ? (
              <Container rootClassName="px-0">
                <PodcastAboutComponent
                  podcastAboutData={podcastAboutData}
                  podcastsComingUpNextData={podcastsComingUpNextData}
                  title={title}
                />
              </Container>
            ) : null}

            {trendingCards && trendingCards.length > 0 ? (
              <Container rootClassName="px-0 no-print">
                <TrendingCards
                  trendingCards={trendingCards}
                  currentArticleId={id}
                  urlPath={urlPath}
                />
              </Container>
            ) : null}

            {isTaboolaEnabled ? (
              <Container rootClassName="px-0 no-print">
                <Taboola
                  container={`below-article-thumbnails-${index}`}
                  placement={`Below Article Thumbnails-${index}`}
                  url={article.urlPath}
                  pageType="article"
                />
              </Container>
            ) : null}
          </div>
        </div>
      </article>
    </>
  );
};

export default MainNewsDefault;
